import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { RegisterFormData, registerSchema } from '@/utils/validations';
import { useRegisterUserMutation, useSendOtpMutation } from '@/store/api/authApi';
import { useOtpTimer } from '@/hooks/useOtpTimer';
import { openModal } from '@/store';

const getErrorMessage = (err: any, fallback: string) => {
  const message = err?.data?.message;
  if (Array.isArray(message)) return message[0] || fallback;
  return message || fallback;
};

export const useRegister = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const referralCode = params.get('refCode');

  const [showOtpField, setShowOtpField] = useState(false);
  const [phone, setPhone] = useState<{
    country?: string;
    countryCode: string;
    fullNumber: string;
    phoneNumber: string;
  }>({
    country: '',
    countryCode: '',
    fullNumber: '',
    phoneNumber: '',
  });

  const { timeLeft, active, start, reset: resetTimer } = useOtpTimer(600);

  const [sendOtp, { isLoading: otpLoading }] = useSendOtpMutation();
  const [registerUser, { isLoading: registerLoading }] = useRegisterUserMutation();

  const form = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      username: '',
      referralCode: '',
      otp: '',
      phone: '',
      password: '',
    },
  });

  const { trigger, reset, resetField, setValue, formState } = form;

  useEffect(() => {
    if (referralCode) {
      setValue('referralCode', referralCode, { shouldValidate: true });
    }
  }, [referralCode, setValue]);

  const handleSendOtp = useCallback(async () => {
    const isValid = await trigger('phone');
    if (!isValid) {
      toast.error(t('auth.register.invalidPhoneToast'));
      return;
    }

    try {
      await sendOtp({
        mobile: phone.fullNumber,
        country: phone.country,
        type: 'register',
      }).unwrap();

      toast.success(t('auth.register.otpSentToast', { phone: phone.fullNumber }));
      setShowOtpField(true);
      start();
    } catch (err: any) {
      toast.error(getErrorMessage(err, t('auth.register.otpFailedToast')));
    }
  }, [trigger, phone, sendOtp, start, t]);

  const handleSubmitForm = async (data: RegisterFormData) => {
    try {
      await registerUser({
        firstname: data.firstName.trim(),
        lastname: data.lastName.trim(),
        username: data.username.trim(),
        password: data.password,
        dialCode: phone.countryCode,
        mobile: phone.fullNumber,
        country: phone.country || 'BD',
        referralCode: data.referralCode,
        mobileVerificationCode: data.otp,
      }).unwrap();

      toast.success(t('auth.register.successToast'));
      reset();
      handleResetOtpField();
      dispatch(openModal({ name: 'login' }));
    } catch (err: any) {
      const message = getErrorMessage(err, t('auth.register.failedToast'));
      if (message.includes('Invalid verification code')) {
        resetField('otp');
      }
      toast.error(message);
    }
  };

  const handleResetOtpField = () => {
    setShowOtpField(false);
    resetTimer();
    resetField('otp');
  };

  return {
    form,
    isValidField: formState.isValid,
    // phone
    phone,
    setPhone,

    //referral code
    referralCode,

    // otp
    showOtpField,
    timeLeft,
    active,
    resetTimer,

    // loaders
    otpLoading,
    registerLoading,

    // handlers
    handleSendOtp,
    handleResetOtpField,
    handleSubmitForm,
  };
};
